import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Modal, Form, Input, message, Space, Typography, Tag, Alert } from 'antd';
import { ShopOutlined, MailOutlined, SendOutlined, EyeOutlined } from '@ant-design/icons';
import { ENDPOINTS } from '../../helpers/api/apiConfig';
import axios from 'axios';

const { Title, Text } = Typography;
const { Column } = Table;

interface CompanyRequest {
    id: number;
    email: string;
    companyName: string;
    companyDomain: string;
    requestDate: string;
    status: string;
}

interface TokenForm {
    email: string;
    companyName: string;
    companyDomain: string;
    salesRepEmail: string;
}

const SalesDashboard: React.FC = () => {
    const [pendingRequests, setPendingRequests] = useState<CompanyRequest[]>([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState<CompanyRequest | null>(null);
    const [form] = Form.useForm();

    useEffect(() => {
        loadPendingRequests();
    }, []);

    const loadPendingRequests = async () => {
        setLoading(true);
        try {
            const response = await axios.get(ENDPOINTS.PENDING_COMPANY_REQUESTS);
            setPendingRequests(response.data.requests);
        } catch (error) {
            console.error('Failed to load pending requests:', error);
            message.error('Failed to load pending requests');
        } finally {
            setLoading(false);
        }
    };

    const handleSendToken = (request: CompanyRequest) => {
        setSelectedRequest(request);
        form.setFieldsValue({
            email: request.email,
            companyName: request.companyName,
            companyDomain: request.companyDomain
        });
        setModalVisible(true);
    };

    const handleTokenSubmit = async (values: TokenForm) => {
        try {
            // Automatically set the sales rep email to accounts@e-nano.io
            const tokenData = {
                ...values,
                salesRepEmail: 'accounts@e-nano.io' // Fixed to the configured sender
            };
            
            await axios.post(ENDPOINTS.SEND_COMPANY_TOKEN, tokenData);
            message.success(`Company token sent successfully to ${values.email}`);
            setModalVisible(false);
            form.resetFields();
            loadPendingRequests(); // Refresh the list
        } catch (error: any) {
            console.error('Failed to send token:', error);
            message.error(error.response?.data?.message || 'Failed to send company token');
        }
    };

    const handleModalClose = () => {
        setModalVisible(false);
        setSelectedRequest(null);
        form.resetFields();
    };

    return (
        <div style={{ padding: '24px' }}>
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <div>
                    <Title level={2}>
                        <ShopOutlined /> Sales Representative Dashboard
                    </Title>
                    <Text type="secondary">
                        Manage company registration requests and send access tokens
                    </Text>
                </div>

                <Alert
                    message="Master Role Access"
                    description="This dashboard is only accessible to users with Master role permissions."
                    type="info"
                    showIcon
                />

                <Card 
                    title="Pending Company Registration Requests" 
                    extra={
                        <Button 
                            onClick={loadPendingRequests} 
                            loading={loading}
                            type="primary"
                        >
                            Refresh
                        </Button>
                    }
                >
                    <Table 
                        dataSource={pendingRequests} 
                        loading={loading}
                        rowKey="id"
                        pagination={{ pageSize: 10 }}
                    >
                        <Column 
                            title="Email" 
                            dataIndex="email" 
                            key="email"
                            render={(email: string) => (
                                <Text copyable>{email}</Text>
                            )}
                        />
                        <Column 
                            title="Company Name" 
                            dataIndex="companyName" 
                            key="companyName"
                            render={(name: string) => (
                                <Text strong>{name}</Text>
                            )}
                        />
                        <Column 
                            title="Domain" 
                            dataIndex="companyDomain" 
                            key="companyDomain"
                            render={(domain: string) => (
                                <Tag color="blue">{domain || 'No domain'}</Tag>
                            )}
                        />
                        <Column 
                            title="Request Date" 
                            dataIndex="requestDate" 
                            key="requestDate"
                            render={(date: string) => (
                                <Text>{new Date(date).toLocaleDateString()}</Text>
                            )}
                        />
                        <Column 
                            title="Status" 
                            dataIndex="status" 
                            key="status"
                            render={(status: string) => (
                                <Tag color={status === 'pending' ? 'orange' : 'green'}>
                                    {status.toUpperCase()}
                                </Tag>
                            )}
                        />
                        <Column
                            title="Actions"
                            key="actions"
                            render={(_, record: CompanyRequest) => (
                                <Space>
                                    <Button
                                        type="primary"
                                        icon={<SendOutlined />}
                                        onClick={() => handleSendToken(record)}
                                        size="small"
                                    >
                                        Send Token
                                    </Button>
                                    <Button
                                        icon={<EyeOutlined />}
                                        onClick={() => {
                                            Modal.info({
                                                title: 'Company Request Details',
                                                content: (
                                                    <div>
                                                        <p><strong>Email:</strong> {record.email}</p>
                                                        <p><strong>Company:</strong> {record.companyName}</p>
                                                        <p><strong>Domain:</strong> {record.companyDomain || 'None'}</p>
                                                        <p><strong>Date:</strong> {new Date(record.requestDate).toLocaleString()}</p>
                                                    </div>
                                                ),
                                            });
                                        }}
                                        size="small"
                                    >
                                        View
                                    </Button>
                                </Space>
                            )}
                        />
                    </Table>
                </Card>

                <Modal
                    title="Send Company Registration Token"
                    open={modalVisible}
                    onCancel={handleModalClose}
                    footer={null}
                    width={600}
                >
                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={handleTokenSubmit}
                    >
                        <Alert
                            message="Token Email Delivery"
                            description="This will create a company registration token valid for 1 week and send it via email to the customer."
                            type="warning"
                            showIcon
                            style={{ marginBottom: 16 }}
                        />

                        <Form.Item
                            name="email"
                            label="Customer Email"
                            rules={[
                                { required: true, message: 'Please enter customer email!' },
                                { type: 'email', message: 'Please enter a valid email!' }
                            ]}
                        >
                            <Input
                                prefix={<MailOutlined />}
                                placeholder="customer@email.com"
                                disabled
                            />
                        </Form.Item>

                        <Form.Item
                            name="companyName"
                            label="Company Name"
                            rules={[{ required: true, message: 'Please enter company name!' }]}
                        >
                            <Input
                                prefix={<ShopOutlined />}
                                placeholder="Company Name"
                            />
                        </Form.Item>

                        <Form.Item
                            name="companyDomain"
                            label="Company Domain"
                        >
                            <Input
                                placeholder="company.com (optional)"
                            />
                        </Form.Item>

                        <Alert
                            message="Automated Email Delivery"
                            description="Company registration tokens will be sent from: accounts@e-nano.io"
                            type="info"
                            showIcon
                            style={{ marginBottom: 16 }}
                        />

                        <Form.Item>
                            <Space>
                                <Button type="primary" htmlType="submit" icon={<SendOutlined />}>
                                    Create & Send Token
                                </Button>
                                <Button onClick={handleModalClose}>
                                    Cancel
                                </Button>
                            </Space>
                        </Form.Item>
                    </Form>
                </Modal>
            </Space>
        </div>
    );
};

export default SalesDashboard;
